import React, {useContext} from 'react';
import {
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useState } from 'react';
import {Button, CircularProgress, TextField} from '@mui/material';
import { useForm } from 'react-hook-form';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as Yup from "yup";
import Paper from "@mui/material/Paper";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {NotificationType, SERVER_URL} from "../../constants/global";
import {UNIQUE_USER} from "../../queries/pharmacy/pharmacyTypeDefs";
import {useMutationUpdateBankAccount} from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import {useAuth} from "../../hooks/useAuth";

interface IBankAccountForm {
  clientSecret: string;
  bankAccountId: number;
  userId: number;
  pharmacy: any;
  pharmacyUsersLoading: boolean;
  setIsEdit: (isEdit: boolean) => void;
  onCancel: () => void;
  onPaymentMethodSaved: () => Promise<void>;
  oldAccountId?: string | null;
}

export const BankAccountForm:React.FunctionComponent<IBankAccountForm> = ({
  clientSecret,
  bankAccountId,
  userId,
  pharmacy,
  pharmacyUsersLoading,
  setIsEdit,
  onCancel,
  onPaymentMethodSaved,
  oldAccountId,
}) => {
  const {addToast} = useContext(ToasterContext);
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [mandate, setMandate] = useState(false);
  const [updateBankAccount, { loading }] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: UNIQUE_USER,
        variables: {
          where: { id: userId },
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const detachOldPaymentMethod = (newPaymentMethodId: string) => {
    if (!oldAccountId || oldAccountId === newPaymentMethodId) return;
    fetch(`${SERVER_URL}/stripe/payment-method/${oldAccountId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      // Best-effort cleanup; the BankAccount row was already overwritten.
    });
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is Required')
      .email('Email is invalid'),
    name: Yup.string()
      .required('Bank account name is Required')
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const handleMandate = async (e: any) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    const { setupIntent, error }: any = await stripe.confirmUsBankAccountSetup(
      clientSecret
    );

    let requestData: any = {
      Pharmacy: {
        connect: {
          id: pharmacy.pharmacyId
        }
      },
      User: {
        connect: {
          id: userId
        }
      },
      accountHolderName: {set: data.payment_method.billing_details.name},
      accountId: {set: data.payment_method.id},
      bankName: {set: data.payment_method.us_bank_account.bank_name},
      country: {set: 'United states'},
      currency: {set: 'USD'},
      last4: {set: data.payment_method.us_bank_account.last4},
      object: {set: data.payment_method.object},
      routingNumber: {set: data.payment_method.us_bank_account.routing_number},
      status: {set: setupIntent.status === 'succeeded' ? 'APPROVED' : 'PENDING'},
      token: {set: data.id},
    };

    if (setupIntent.next_action) {
      requestData = {
        ...requestData,
        hostedVerificationUrl: { set: setupIntent.next_action.verify_with_microdeposits.hosted_verification_url },
      }
    }

    if (error) {
      addToast({ type: NotificationType.Error, title: error.message  });
      setIsEdit(false);
    } else if (setupIntent.status === 'requires_payment_method') {
      addToast({ type: NotificationType.Error, title: 'Confirmation failed. Attempt again with a different payment method.'  });
      setIsEdit(false);
    } else if (setupIntent.status === 'succeeded') {
      try {
        await updateBankAccount({ variables: { data: requestData, where: { id: bankAccountId } } });
        detachOldPaymentMethod(data.payment_method.id);
        addToast({ type: NotificationType.Success, title: 'Bank account successfully created.' });
        await onPaymentMethodSaved();
      } catch (error: any) {
        addToast({ type: NotificationType.Error, title: error.message });
        setIsEdit(false);
      }
    } else if (setupIntent.next_action?.type === 'verify_with_microdeposits') {
      try {
        await updateBankAccount({ variables: { data: requestData, where: { id: bankAccountId } } });
        detachOldPaymentMethod(data.payment_method.id);
        addToast({
          type: NotificationType.Success,
          title: 'Bank account successfully created. Please verify your account.',
        });
        await onPaymentMethodSaved();
      } catch (error: any) {
        addToast({ type: NotificationType.Error, title: error.message });
        setIsEdit(false);
      }
    }
  };

  const handleForm = async (data: any) => {
    setIsLoading(true);

    if (!stripe || !elements) {
      setIsLoading(false);

      return;
    }

    const { setupIntent, error } = await stripe.collectBankAccountForSetup({
      clientSecret: clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        payment_method_data: {
          billing_details: {
            name: data.name,
            email: data.email,
          },
        },
      },
      expand: ['payment_method'],
    });

    setData(setupIntent);

    if (error) {
      setIsLoading(false);
      addToast({ type: NotificationType.Error,  title: error.message });
    } else if (setupIntent.status === 'requires_payment_method') {
      setIsLoading(false);
      addToast({ type: NotificationType.Error,  title: 'Confirmation failed. Attempt again with a different payment method.' });
    } else if (setupIntent.status === 'requires_confirmation') {
      setIsLoading(false);
      setMandate(true);
    }
  };

  return (
    <Paper sx={{p: 4}}>
      {!mandate ? (
        <Box component="form" onSubmit={handleSubmit(handleForm)} noValidate sx={{mt: 2}}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                id="email"
                fullWidth
                required
                helperText={errors.email?.message?.toString()}
                {...register('email')}
                error={!!errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Account Name"
                id="name"
                fullWidth
                required
                helperText={errors.name?.message?.toString()}
                {...register('name')}
                error={!!errors.name}
              />
            </Grid>
          </Grid>
          <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
            <Button type="submit" variant="contained" size="large" disabled={isLoading}>
              Add Bank Account
              {isLoading && <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />}
            </Button>
            <Button variant="outlined" size="large" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
          </Grid>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleMandate} noValidate sx={{mt: 2}}>
          <p>
            By clicking [Accept], you authorize TradeNetRx to Credit the bank
            account specified above for any amount owed for charges arising from
            your use of TradenetRx’ services and/or purchase of products from
            TradenetRx, pursuant to TradenetRx’ website and terms, until this
            authorization is revoked. You may amend or cancel this authorization
            at any time by providing notice to TradenetRx with 30 (thirty) days
            notice.{' '}
          </p>
          <p>
            If you use TradenetRx’ services or purchase additional products
            periodically pursuant to TradenetRx’ terms, you authorize TradenetRx
            to debit your bank account periodically. Payments that fall outside
            of the regular debits authorized above will only be debited after
            your authorization is obtained.
          </p>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Button type="submit" variant="contained" size="large">
              Accept
              {(isLoading || loading || pharmacyUsersLoading) && (
                <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />
              )}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setMandate(false)}
              disabled={isLoading || loading}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={onCancel}
              disabled={isLoading || loading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default BankAccountForm;
